// Catalog.ID Client — Key Grant Supersecret Prompt
// ============================================================================
//
// Save mode (and any future flow that mutates the keybag) needs the
// supersecret-derived AES-256 key to encrypt new entries consistently with
// the rest of the bag. We deliberately DO NOT cache the derived key
// across calls — adding entries is rare, and re-asking each time is a
// reasonable confirmation that the user really meant to mutate the bag.
//
// The caller MUST `fill(0)` the returned derivedKey as soon as it's done
// using it. This module also zeroes its own intermediate buffers.
// ============================================================================

// `ShellAPI` rather than the `Shell` class: this prompt is opened from pane
// title rows (`ctx.shell` is the interface) as well as from the key-grant
// client (which holds the class). Only `showModal` is used, and both declare it
// identically.
import type { ShellAPI } from '@catalog-shell';
import { createElement } from '@catalog-shell';
import { fromBase64 } from '../crypto/aes';
import { deriveKeyFromSupersecret } from '../crypto/argon2';
import { decryptEntry, type EncryptedKeyBagEntry } from '../crypto/keybag';
import { createSupersecretTextarea } from '../components/supersecret-textarea';

export interface SupersecretPromptResult {
    derivedKey: Uint8Array;  // caller MUST zeroise after use
    saltB64: string;
}

export interface SupersecretPromptInput {
    /** keySalt from GET /api/account/keybag, base64. */
    saltB64: string;
    /**
     * One of the encrypted entries from GET /api/account/keybag, used to
     * verify the derived key (AES-GCM tag check). MUST be a non-revoked
     * entry — revoked entries may have been encrypted under an older
     * derivation. Pass null to skip verification (only when the bag is
     * empty — should not happen in practice).
     */
    verifyAgainst: EncryptedKeyBagEntry | null;
    /** Title shown at the top of the modal. */
    title?: string;
    /** Body text under the title; defaults to a generic save-mode message. */
    description?: string;
}

/**
 * Show a modal prompting for the supersecret, derive the AES-256 key, verify
 * by attempting to decrypt the supplied <see cref="EncryptedKeyBagEntry"/>,
 * and resolve with the derived key + salt. Returns null on cancel / Escape.
 *
 * The verification step is what catches typos and wrong supersecrets cleanly:
 * AES-GCM tag mismatch on decryptEntry → tell the user "wrong supersecret"
 * and let them retry inline, no retry-loop bookkeeping needed.
 */
export function promptAndDeriveKey(
    shell: ShellAPI,
    input: SupersecretPromptInput,
): Promise<SupersecretPromptResult | null> {
    return new Promise<SupersecretPromptResult | null>((resolve) => {
        let resolved = false;
        const resolveOnce = (r: SupersecretPromptResult | null): void => {
            if (resolved) return;
            resolved = true;
            handle.close();
            resolve(r);
        };

        // Root class matters: panes.less lets only .cid-pane-content / .cid-wizard-step roots
        // shrink to the 400 px card (min-width 0, border-box); a bare div with an inline
        // min-width overflowed and .cs-modal-card { overflow-x: hidden } clipped its right half.
        const card = createElement('div', { className: 'cid-pane-content', style: { padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' } });

        card.appendChild(createElement('h2', {
            className: 'cs-modal-title',
            textContent: input.title ?? 'Enter your supersecret',
            style: { margin: '0', fontSize: '18px' },
        }));

        card.appendChild(createElement('p', {
            className: 'cid-auth-subtitle',
            textContent: input.description
                ?? 'Your supersecret is required to add a new key to your key bag. ' +
                   'It is used here only to derive the encryption key, then discarded.',
            style: { fontSize: '13px', opacity: '0.85', margin: '0' },
        }));

        const ss = createSupersecretTextarea({ autoFocus: true });
        card.appendChild(ss.element);
        const ssInput = ss.textarea;

        const errorEl = createElement('div', {
            style: { color: 'var(--cs-danger, #ff7676)', fontSize: '13px', minHeight: '18px' },
        });
        card.appendChild(errorEl);

        const footer = createElement('div', { style: { display: 'flex', gap: '8px', justifyContent: 'flex-end' } });
        const cancelBtn = createElement('button', {
            className: 'cs-btn cs-btn--default',
            textContent: 'Cancel',
        });
        cancelBtn.addEventListener('click', () => resolveOnce(null));
        footer.appendChild(cancelBtn);

        const submitBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes',
            textContent: 'Unlock',
        }) as HTMLButtonElement;
        submitBtn.disabled = true;
        ss.onValidityChange(valid => { submitBtn.disabled = !valid; });
        footer.appendChild(submitBtn);
        card.appendChild(footer);

        async function attempt(): Promise<void> {
            errorEl.textContent = '';
            const supersecret = ss.getValue();
            if (!ss.isValid()) {
                // Defensive — the disabled gate should already prevent this.
                ssInput.focus();
                return;
            }

            submitBtn.disabled = true;
            submitBtn.textContent = 'Deriving…';
            cancelBtn.setAttribute('disabled', '');

            let salt: Uint8Array;
            try {
                salt = fromBase64(input.saltB64);
            } catch {
                errorEl.textContent = 'Internal error: bad keySalt.';
                submitBtn.disabled = false;
                submitBtn.textContent = 'Unlock';
                cancelBtn.removeAttribute('disabled');
                return;
            }

            let derivedKey: Uint8Array;
            try {
                derivedKey = await deriveKeyFromSupersecret(supersecret, salt);
            } catch (e) {
                errorEl.textContent = `Argon2 derivation failed: ${(e as Error)?.message ?? 'unknown'}`;
                submitBtn.disabled = false;
                submitBtn.textContent = 'Unlock';
                cancelBtn.removeAttribute('disabled');
                return;
            }

            // AES-GCM tag mismatch on decrypt = wrong supersecret. We zero
            // the freshly-derived (incorrect) key before returning to the
            // input prompt so a discarded wrong key doesn't linger.
            if (input.verifyAgainst) {
                try {
                    await decryptEntry(input.verifyAgainst, derivedKey);
                } catch {
                    derivedKey.fill(0);
                    errorEl.textContent = 'Wrong supersecret — try again.';
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Unlock';
                    cancelBtn.removeAttribute('disabled');
                    ssInput.select();
                    ssInput.focus();
                    return;
                }
            }

            // Caller takes ownership of the key bytes and is responsible
            // for zeroing on use (see module header).
            resolveOnce({ derivedKey, saltB64: input.saltB64 });
        }

        submitBtn.addEventListener('click', () => { void attempt(); });
        ssInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void attempt(); }
            if (e.key === 'Escape') { e.preventDefault(); resolveOnce(null); }
        });

        const handle = shell.showModal(card, {
            size: 'normal',
            dismissOnOverlayClick: false,
            dismissOnEscape: false,
            onClose: () => resolveOnce(null),
        });
    });
}
